import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CsvInput } from "@/components/common/FormField";

/**
 * Editable list of objects (products, offers, personas, competitors, FAQ...).
 * columns: [{ key, label, type: 'text'|'textarea'|'csv'|'select', options?: [{value,label}], placeholder?, span? }]
 */
export const RepeaterField = ({ id, label, hint, rows, onChange, columns, addLabel, emptyLabel, testId }) => {
  const { t } = useTranslation();
  const items = Array.isArray(rows) ? rows : [];
  const update = (index, key, value) => onChange(items.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () => onChange([...items, Object.fromEntries(columns.map((c) => [c.key, c.type === "csv" ? [] : ""]))]);
  const tid = testId || id;

  return (
    <div className="space-y-2" data-testid={tid}>
      {label && (
        <div>
          <Label className="text-sm font-medium">{label}</Label>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      )}
      {items.length === 0 && emptyLabel && <p className="rounded-lg border border-dashed border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">{emptyLabel}</p>}
      <div className="space-y-3">
        {items.map((row, index) => (
          <div key={index} className="rounded-lg border border-border bg-background p-3" data-testid={`${tid}-row-${index}`}>
            <div className="grid gap-3 sm:grid-cols-6">
              {columns.map((col) => {
                const value = row?.[col.key] ?? (col.type === "csv" ? [] : "");
                const span = col.span || (col.type === "textarea" ? "sm:col-span-6" : "sm:col-span-3");
                const cid = `${tid}-${index}-${col.key}`;
                return (
                  <div key={col.key} className={`space-y-1 ${span}`}>
                    <Label htmlFor={cid} className="text-xs text-muted-foreground">{col.label}</Label>
                    {col.type === "textarea" ? (
                      <Textarea id={cid} rows={2} value={value} placeholder={col.placeholder} onChange={(e) => update(index, col.key, e.target.value)} data-testid={cid} className="resize-y" />
                    ) : col.type === "csv" ? (
                      <CsvInput id={cid} value={Array.isArray(value) ? value : (value ? String(value).split(",").map((s) => s.trim()).filter(Boolean) : [])} placeholder={col.placeholder || t("common.commaSeparated")} onChange={(arr) => update(index, col.key, arr)} testId={cid} />
                    ) : col.type === "select" ? (
                      <Select value={value || ""} onValueChange={(v) => update(index, col.key, v)}>
                        <SelectTrigger id={cid} data-testid={cid} className="bg-card"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                        <SelectContent className="bg-card">
                          {col.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input id={cid} value={value} placeholder={col.placeholder} onChange={(e) => update(index, col.key, e.target.value)} data-testid={cid} />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-end">
              <Button type="button" variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-rose-900" onClick={() => remove(index)} data-testid={`${tid}-remove-${index}`}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />{t("common.remove")}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="bg-card" onClick={add} data-testid={`${tid}-add`}>
        <Plus className="mr-1.5 h-4 w-4" />{addLabel || t("common.add")}
      </Button>
    </div>
  );
};
